import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '@app/core/services/api/api.service';
import {
  AllTestsOverviewResponse,
  TestListItem,
  TestFilters,
  PaginationInfo,
  DEFAULT_PAGE_SIZE,
} from '../all-tests-dashboard.models';

export interface TestListResponse {
  tests: TestListItem[];
  pagination: PaginationInfo;
  runningTestsCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class AllTestsService {
  constructor(private apiService: ApiService) {}

  /**
   * Fetch test list with server-side filtering and pagination
   * @param filters - Filter and pagination criteria
   * @returns Observable of tests with pagination info
   */
  getTestList(filters?: TestFilters): Observable<TestListResponse> {
    const queryParams = this.buildQueryParams(filters);
    const endpoint = `analytics/all-abtests-metric${queryParams}`;

    return this.apiService.get(endpoint).pipe(
      map((response: AllTestsOverviewResponse) => ({
        tests: response.tests,
        pagination: response.pagination,
        runningTestsCount: response.runningTestsCount ?? 0,
      })),
      catchError((error) => {
        console.error('Failed to fetch test list', error);
        throw error;
      }),
    );
  }

  private buildQueryParams(filters?: TestFilters): string {
    const params = new URLSearchParams();

    if (filters?.startDate) {
      params.append('startDate', filters.startDate);
    }

    if (filters?.endDate) {
      params.append('endDate', filters.endDate);
    }

    if (filters?.status && filters.status !== 'all') {
      params.append('status', filters.status);
    }

    if (filters?.testType && filters.testType !== 'all') {
      params.append('testType', filters.testType);
    }

    if (filters?.searchQuery) {
      params.append('searchQuery', filters.searchQuery);
    }

    if (filters?.sortColumn) {
      params.append('sortColumn', filters.sortColumn);
    }

    if (filters?.sortDirection) {
      params.append('sortDirection', filters.sortDirection);
    }

    const page = filters?.page ?? 0;
    const pageSize = filters?.pageSize ?? DEFAULT_PAGE_SIZE;
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());

    return params.toString() ? `?${params.toString()}` : '';
  }
}
